import { useTranslation } from 'react-i18next'
import { Sparkles, Download, Trash2, Maximize2 } from 'lucide-react'
import type { SlotGeneration } from '@/lib/api/amazon-listing.types'
import { downloadSlotGeneration, triggerBlobDownload } from '@/lib/api/amazon-listing'

interface SlotGenerationThumbnailProps {
  slotId: string
  gen: SlotGeneration
  isSelected: boolean
  onSelect: (id: string) => void
  onOpenLightbox: () => void
  onDelete?: (id: string) => void
}

export function SlotGenerationThumbnail({
  slotId,
  gen,
  isSelected,
  onSelect,
  onOpenLightbox,
  onDelete,
}: SlotGenerationThumbnailProps) {
  const { t } = useTranslation()

  const formattedDate = (() => {
    try {
      const d = new Date(gen.created_at)
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const hours = String(d.getHours()).padStart(2, '0')
      const mins = String(d.getMinutes()).padStart(2, '0')
      return `${day}/${month}, ${hours}:${mins}`
    } catch {
      return ''
    }
  })()

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      const blob = await downloadSlotGeneration(slotId, gen.id)
      triggerBlobDownload(blob, `generation-${gen.id}.png`)
    } catch { /* silent - toast could be added */ }
  }

  return (
    <div
      className={`relative cursor-pointer rounded-xl overflow-hidden border-2 transition-colors group ${isSelected ? 'border-orange-500' : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300'}`}
      onClick={() => onSelect(gen.id)}
    >
      <div className="relative w-full aspect-square">
        <img src={gen.asset_url} alt="" className="w-full h-full object-cover" />

        {/* Hover-Overlay mit Aktions-Buttons */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpenLightbox() }}
            className="p-1.5 rounded-full bg-white/90 hover:bg-white text-neutral-800 transition-colors"
            aria-label="Vergrößern"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="p-1.5 rounded-full bg-white/90 hover:bg-white text-neutral-800 transition-colors"
            aria-label="Herunterladen"
          >
            <Download className="w-3.5 h-3.5" />
          </button>
          {onDelete && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(gen.id) }}
              className="p-1.5 rounded-full bg-white/90 hover:bg-red-50 text-red-500 transition-colors"
              aria-label="Löschen"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="absolute top-1.5 left-1.5">
          <span className="bg-white/80 dark:bg-neutral-900/80 rounded-full p-0.5 flex items-center justify-center w-5 h-5">
            <Sparkles className="w-3 h-3 text-orange-400" />
          </span>
        </div>
        {gen.fidelity_score != null && (
          <div className="absolute top-1.5 right-1.5 bg-black/60 backdrop-blur-sm rounded-md px-1.5 py-0.5 text-2xs text-white">
            {gen.fidelity_score}%
          </div>
        )}
      </div>
      <div className="px-2 py-1.5 bg-white dark:bg-neutral-900 flex items-center justify-between">
        <p className="text-2xs text-neutral-400">{formattedDate}</p>
        {isSelected && (
          <p className="text-2xs text-orange-500 font-semibold">{t('amazonListing.slots.selected')}</p>
        )}
      </div>
    </div>
  )
}
